// FILE: apps/web/src/pages/CarersPage.tsx
// Carers list page — availability, skills, city, active toggle. Server-driven via DataTable.

import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import type { JSX } from "react";
import { toast } from "sonner";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import { NewCarerModal } from "../components/NewCarerModal.js";
import { CarerScheduleDrawer } from "../components/CarerScheduleDrawer.js";
import { SwitchToggle } from "../components/SwitchToggle.js";
import { AlertDialog } from "../components/AlertDialog.js";
import { useHighlightedRow } from "../hooks/useHighlightedRow.js";
import {
  DataTable,
  useDataTable,
  type ColumnDef,
  type ListQuery,
} from "../components/DataTable/index.js";

type DaySchedule = Array<{ start: string; end: string }>;

interface Carer {
  _id: string;
  full_name?: string;
  primary_phone?: string;
  is_available?: boolean;
  is_active?: boolean;
  skills?: string[];
  city?: string;
  tier?: number;
  availability_schedule?: Partial<Record<string, DaySchedule>>;
}

interface CarerListResponse { data: Carer[]; total: number; page: number; limit: number; }

function buildQs(query: ListQuery): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v == null || v === "") continue;
    p.set(k, String(v));
  }
  return p.toString();
}

function useCarers(query: ListQuery): ReturnType<typeof useQuery<CarerListResponse>> {
  const qs = buildQs(query);
  return useQuery<CarerListResponse>({
    queryKey: ["carers", qs],
    queryFn: () => apiFetch<CarerListResponse>(`/carers?${qs}`),
  });
}

function AvailPill({ isAvailable }: { isAvailable?: boolean }): JSX.Element {
  const label = isAvailable ? "Available" : "Unavailable";
  const cls   = isAvailable ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600";
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>;
}

interface CarerActions {
  onSchedule: (c: Carer) => void;
  onToggleActive: (id: string, newValue: boolean) => void;
  onEdit: (id: string) => void;
}

function buildColumns(
  actions: CarerActions,
): ReadonlyArray<ColumnDef<Carer>> {
  const cols: Array<ColumnDef<Carer>> = [
    { accessorKey: "full_name", header: "Name",
      cell: (c) => (
        <Link to={`/carers/${c._id}`} className="text-navy-700 font-medium hover:underline">
          {c.full_name ?? "—"}
        </Link>
      ) },
    { accessorKey: "primary_phone", header: "Phone",
      cell: (c) => <span className="text-slate-600 font-mono text-xs">{c.primary_phone ?? "—"}</span> },
    { accessorKey: "is_available", header: "Availability",
      cell: (c) => <AvailPill isAvailable={c.is_available} /> },
    { accessorKey: "skills", header: "Skills",
      cell: (c) => <span className="text-slate-600 text-sm">{c.skills?.join(", ") || "—"}</span> },
    { accessorKey: "city", header: "City",
      cell: (c) => <span className="text-slate-600">{c.city ?? "—"}</span> },
  ];
  cols.push(
    { accessorKey: "is_active", header: "Active",
      cell: (c) => (
        <SwitchToggle label="Active" checked={c.is_active !== false}
          onChange={(v) => actions.onToggleActive(c._id, v)} />
      ) },
    { accessorKey: "actions", header: "", align: "right",
      cell: (c) => (
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => actions.onEdit(c._id)}
            data-testid={`carers-edit-${c._id}-btn`}
            className="text-xs text-slate-600 hover:text-slate-800 font-medium">
            Edit
          </button>
          <button type="button" onClick={() => actions.onSchedule(c)}
            data-testid={`carers-schedule-${c._id}-btn`}
            className="text-xs text-navy-700 hover:text-navy-800 font-medium">
            Schedule
          </button>
        </div>
      ) },
  );
  return cols;
}

function useCarerToggleMutation(items: readonly Carer[]): ReturnType<typeof useMutation<unknown, Error, { id: string; is_active: boolean }>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      apiFetch<unknown>(`/carers/${id}`, { method: "PATCH", body: JSON.stringify({ is_active }) }),
    onSuccess: (_, { is_active, id }) => {
      void qc.invalidateQueries({ queryKey: ["carers"] });
      const name = items.find((c) => c._id === id)?.full_name ?? "Carer";
      toast.success(is_active ? `${name} reactivated.` : `${name} deactivated.`);
    },
  });
}

// Server response shape for GET /carers/:id — narrowed to fields the
// modal cares about. Optional everywhere because legacy / partial docs
// may omit some keys (modal falls back to FormState defaults).
interface CarerDetail {
  _id: string;
  full_name?: string;
  primary_phone?: string;
  city?: string;
  gender?: string;
  languages_spoken?: string[];
  skills?: string[];
  qualifications?: string[];
  training_completed?: string[];
  specialist_skills?: string[];
  tier?: number;
  coverage_pincodes?: string[];
  years_experience?: number;
  photo_url?: string;
  id_verified?: boolean;
  background_verified_date?: string;
  contract_expiry_date?: string;
  onboarding_checklist?: string[];
  agency_id?: string;
}

// Maps GET /carers/:id response into the modal's FormState shape.
// Each value is sourced from `d` if defined, else from the appropriate
// default. Driven by a dispatch table to stay under the cyclomatic +
// cognitive complexity caps; per-field ?? operators added one branch
// each and pushed past the 10-line threshold.
const CARER_DEFAULTS: Record<string, unknown> = {
  full_name: "", primary_phone: "", city: "", gender: "",
  languages_spoken: [], skills: [], qualifications: [],
  training_completed: [], specialist_skills: [],
  tier: "1", coverage_pincodes: [], years_experience: "",
  photo_url: "", id_verified: false, background_verified_date: "",
  contract_expiry_date: "", onboarding_checklist: [], agency_id: "",
};
const NUMERIC_FIELDS = new Set<keyof CarerDetail>(["tier", "years_experience"]);

function carerDetailToInitialValues(d: CarerDetail): Record<string, unknown> {
  const out: Record<string, unknown> = { ...CARER_DEFAULTS };
  for (const key of Object.keys(CARER_DEFAULTS)) {
    const v = d[key as keyof CarerDetail];
    if (v === undefined) continue;
    out[key] = NUMERIC_FIELDS.has(key as keyof CarerDetail) ? String(v) : v;
  }
  return out;
}

interface OverlayProps {
  showNew: boolean;
  onCloseNew: () => void;
  onCarerCreated: () => void;
  editingCarerId: string | null;
  editingCarerData: CarerDetail | null;
  onCloseEdit: () => void;
  onCarerUpdated: () => void;
  schedCarer: Carer | null;
  onCloseSched: () => void;
  deactivating: Carer | null;
  onConfirmDeactivate: () => void;
  onCancelDeactivate: () => void;
  isPending: boolean;
}

function CarerOverlays(p: OverlayProps): JSX.Element {
  return (
    <>
      {p.showNew && <NewCarerModal onClose={p.onCloseNew} onCreated={p.onCarerCreated} />}
      {p.editingCarerId && p.editingCarerData && (
        <NewCarerModal
          mode="edit"
          carerId={p.editingCarerId}
          initialValues={carerDetailToInitialValues(p.editingCarerData)}
          onClose={p.onCloseEdit}
          onCreated={p.onCarerUpdated}
        />
      )}
      {p.schedCarer && (
        <CarerScheduleDrawer
          carerId={p.schedCarer._id}
          carerName={p.schedCarer.full_name ?? ""}
          initialSchedule={p.schedCarer.availability_schedule ?? {}}
          onClose={p.onCloseSched}
        />
      )}
      {p.deactivating && (
        <AlertDialog
          title={`Deactivate ${p.deactivating.full_name ?? "this carer"}?`}
          description="They will not appear in assignment suggestions."
          confirmLabel="Deactivate"
          onConfirm={p.onConfirmDeactivate}
          onCancel={p.onCancelDeactivate}
          isPending={p.isPending}
        />
      )}
    </>
  );
}

export function CarersPage(): JSX.Element {
  const [showNew, setShowNew]           = useState(false);
  const [schedCarer, setSchedCarer]     = useState<Carer | null>(null);
  const [deactivating, setDeactivating] = useState<Carer | null>(null);
  const [editingCarerId, setEditingCarerId] = useState<string | null>(null);
  const { highlightId } = useHighlightedRow();
  const qc = useQueryClient();

  const tableState = useDataTable({
    pageSize: 20,
    defaultSort: { sortBy: "created_at", order: "desc" },
  });
  const { data, isLoading, isError } = useCarers(tableState.query);
  const items: Carer[] = data?.data ?? [];
  const toggleMutation = useCarerToggleMutation(items);

  // Edit-mode preflight fetch — only fires when an Edit button is clicked.
  // Response populates the modal's initialValues; modal mounts only after
  // the fetch resolves so the form is never empty in edit mode.
  const { data: editingCarerData } = useQuery<CarerDetail>({
    queryKey: ["carer-detail", editingCarerId],
    queryFn: () => apiFetch<CarerDetail>(`/carers/${editingCarerId ?? ""}`),
    enabled: editingCarerId !== null,
  });

  function handleToggleActive(id: string, newValue: boolean): void {
    if (!newValue) setDeactivating(items.find((c) => c._id === id) ?? { _id: id });
    else toggleMutation.mutate({ id, is_active: true });
  }

  function handleConfirmDeactivate(): void {
    if (!deactivating) return;
    toggleMutation.mutate({ id: deactivating._id, is_active: false });
    setDeactivating(null);
  }

  function handleCarerUpdated(): void {
    setEditingCarerId(null);
    void qc.invalidateQueries({ queryKey: ["carers"] });
    void qc.invalidateQueries({ queryKey: ["carer-detail"] });
    toast.success("Carer updated.");
  }

  const columns = buildColumns(
    { onSchedule: setSchedCarer, onToggleActive: handleToggleActive, onEdit: setEditingCarerId },
  );

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-slate-900">
            Carers <span className="text-sm font-normal text-slate-400">({data ? data.total : 0})</span>
          </h1>
          <button onClick={() => setShowNew(true)} data-testid="carers-new-btn"
            className="bg-navy-700 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-navy-800 transition-colors">
            + New Carer
          </button>
        </div>
        <DataTable<Carer>
          state={tableState} columns={columns} data={items} total={data?.total ?? 0}
          rowKey={(c) => c._id} ariaLabel="Carers"
          isLoading={isLoading} isError={isError}
          errorMessage="Failed to load carers."
          emptyState="No carers yet. Add your first carer."
          searchPlaceholder="Search by name, phone, ref, or city"
          highlightId={highlightId ?? undefined}
        />
      </div>
      <CarerOverlays
        showNew={showNew}
        onCloseNew={() => setShowNew(false)}
        onCarerCreated={() => { setShowNew(false); void qc.invalidateQueries({ queryKey: ["carers"] }); }}
        editingCarerId={editingCarerId}
        editingCarerData={editingCarerData ?? null}
        onCloseEdit={() => setEditingCarerId(null)}
        onCarerUpdated={handleCarerUpdated}
        schedCarer={schedCarer}
        onCloseSched={() => setSchedCarer(null)}
        deactivating={deactivating}
        onConfirmDeactivate={handleConfirmDeactivate}
        onCancelDeactivate={() => setDeactivating(null)}
        isPending={toggleMutation.isPending}
      />
    </Shell>
  );
}
